import { Box, CircularProgress, Typography, alpha } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useOutletContext } from 'react-router-dom';

import {
    getCatalogItems,
    getCatalogItemsAreLoading,
    getCatalogItemsLoadingMore,
    getSelectedCatalogItems,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import {
    getSelectCategoryId,
    getSelectCategoryTitle,
    getSelectSubCategoryId,
    getSelectSubCategoryName,
} from 'entities/Category/model/selectors/categorySelectors';
import { getUserCanManageCatalogItems } from 'entities/User/model/selectors/userPermissionsSeletors';
import { useGlobalCatalogInfiniteScroll } from 'features/SearchCatalogItems/lib/hooks/useGlobalCatalogInfiniteScroll';
import {
    getSearchGlobalCatalogItemValue,
    getSearchLocalCatalogItemValue,
} from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import {
    type CatalogCategoryChangeFollowUp,
    ChangeCatalogItemCategoryButton,
} from 'features/changeCatalogItemCategory';
import { MoveToInventoryCatalogItemButton } from 'features/moveToInventoryCatalogItem';
import { RemoveCatalogItem } from 'features/removeCatalogItem';
import { getRouteGlobalCatalog } from 'shared/const/router';
import { useIsOrdersAddItemsMarketplace } from 'shared/lib/contexts/CatalogIdentityContext';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';

import { PropertyCatalogItemsList } from './PropertyCatalogItemsList';

interface PropertyCatalogItemsOutletProps {
    hasCatalogItems: boolean;
    showBulkPanel: boolean;
    showActionPanel?: boolean;
    moveTo: PropertyCatalogItemsMoveTo;
    onAfterCatalogCategoryChange?: CatalogCategoryChangeFollowUp;
}

/** Matches the absolute selected-items footer minHeight. */
const ACTION_PANEL_OVERLAY_HEIGHT_PX = 58;

export const PropertyCatalogItemsOutlet = () => {
    const { t } = useTranslation();
    const {
        hasCatalogItems,
        showBulkPanel,
        showActionPanel = false,
        moveTo,
        onAfterCatalogCategoryChange,
    } = useOutletContext<PropertyCatalogItemsOutletProps>();

    const selectedItems = useSelector(getSelectedCatalogItems);
    const location = useLocation();
    const catalogItems = useSelector(getCatalogItems);
    const loading = useSelector(getCatalogItemsAreLoading);
    const isLoadingMore = useSelector(getCatalogItemsLoadingMore);
    const categoryTitle = useSelector(getSelectCategoryTitle);
    const subCategoryName = useSelector(getSelectSubCategoryName);
    const selectedCategoryId = useSelector(getSelectCategoryId);
    const selectedSubCategoryId = useSelector(getSelectSubCategoryId);
    const globalSearchValue = useSelector(getSearchGlobalCatalogItemValue);
    const localSearchValue = useSelector(getSearchLocalCatalogItemValue);
    const canEditCatalog = useSelector(getUserCanManageCatalogItems);
    const isGlobalCatalogRoute = useMatch(
        getRouteGlobalCatalog(':propertyId') + '/*',
    );
    const isOrderGlobalCatalog = useIsOrdersAddItemsMarketplace();
    const isGlobalCatalogMode = Boolean(
        isGlobalCatalogRoute || isOrderGlobalCatalog,
    );
    const currentPageIsOrders = useMatch('/orders' + '/*');
    const { onScroll: onGlobalCatalogScroll } = useGlobalCatalogInfiniteScroll({
        enabled: isGlobalCatalogMode,
    });

    const categoryHeaderText = useMemo(() => {
        if (!categoryTitle) {
            return null;
        }

        if (isGlobalCatalogMode && subCategoryName) {
            return `${categoryTitle} / ${subCategoryName}`;
        }

        return categoryTitle;
    }, [categoryTitle, isGlobalCatalogMode, subCategoryName]);

    const isSingleItem = Object.keys(selectedItems || {})?.length === 1;
    const isOrderEditing = location.state?.selectedOrderId;

    const showBulkActionsRow =
        hasCatalogItems && !isGlobalCatalogMode && showBulkPanel;

    const scrollableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollableRef.current) {
            scrollableRef.current.scrollTo(0, 0);
        }
    }, [
        selectedCategoryId,
        selectedSubCategoryId,
        globalSearchValue,
        localSearchValue,
    ]);

    return (
        <Box
            ref={scrollableRef}
            onScroll={onGlobalCatalogScroll}
            sx={{
                flex: '1 1',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'scroll',
                paddingTop: 0,
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingBottom: showActionPanel
                    ? `${ACTION_PANEL_OVERLAY_HEIGHT_PX + 16}px`
                    : '16px',
            }}
        >
            {categoryHeaderText || showBulkActionsRow ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        columnGap: '16px',
                        rowGap: '8px',
                        flexWrap: 'wrap',
                        padding: '10px 16px',
                        bgcolor: 'background.paper',
                        zIndex: 'drawer',
                        position: 'sticky',
                        top: 0,
                        marginLeft: '-16px',
                        marginRight: '-16px',
                        borderTopRightRadius: '12px',
                    }}
                >
                    {categoryHeaderText ? (
                        <Typography
                            data-testclass="catalog-items-category-header"
                            typography="poppins.subtitle2"
                            color="text.primary"
                            sx={{
                                flex: '1 1 auto',
                                minWidth: 0,
                                lineHeight: '36px',
                            }}
                        >
                            {categoryHeaderText}
                        </Typography>
                    ) : (
                        <Box sx={{ flex: '1 1 auto' }} />
                    )}
                    {showBulkActionsRow ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                columnGap: '12px',
                                rowGap: '8px',
                                flexShrink: 0,
                            }}
                        >
                            <RemoveCatalogItem isSingleItem={isSingleItem} />
                            {canEditCatalog ? (
                                <ChangeCatalogItemCategoryButton
                                    visualVariant="propertyCatalogToolbar"
                                    selectedItems={selectedItems}
                                    onAfterCatalogCategoryChange={
                                        onAfterCatalogCategoryChange
                                    }
                                    data-testclass="property-catalog-bulk-edit-category-button"
                                />
                            ) : null}
                            <MoveToInventoryCatalogItemButton
                                isSingleItem={isSingleItem}
                            />
                        </Box>
                    ) : null}
                </Box>
            ) : null}
            <PropertyCatalogItemsList
                items={catalogItems}
                loading={loading}
                moveTo={moveTo}
                quickEdit={isOrderEditing}
                isOrderPage={!!currentPageIsOrders}
            />
            {isGlobalCatalogMode && isLoadingMore && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '12px',
                        paddingTop: '20px',
                        paddingBottom: '20px',
                    }}
                >
                    <CircularProgress size={14} />
                    <Typography
                        typography="openSans.body2Medium"
                        sx={{
                            color: (theme) =>
                                alpha(theme.palette.common.black, 0.5),
                            fontWeight: 400,
                        }}
                    >
                        {t('Loading more items')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
